import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { assistantService } from '../../services/AssistantService';
import { publishCopilotChange } from '../../lib/copilotSyncBus';
import { StoredConversation, getConversation, listConversations, saveConversation, deleteConversation as deleteStoredConversation } from './conversationStore';
import { AssistantChatResult, AssistantComplexity, AssistantLiveStep, AssistantPendingAction, AssistantPageContext, AssistantSuggestedAction, AssistantTurn, ChatMessage } from '../../interfaces/Assistant';

const SESSION_KEY = 'assistant-session-id';
const OPEN_KEY = 'assistant-open';
const COMPLEXITY_KEY = 'assistant-complexity';

function readComplexity(): AssistantComplexity {
  try {
    const v = localStorage.getItem(COMPLEXITY_KEY);
    if (v === 'simple' || v === 'advanced' || v === 'normal') return v;
  } catch {
    /* ignore */
  }
  return 'normal';
}

let counter = 0;
const nextId = () => `m_${Date.now()}_${counter++}`;

/** Derivă contextul paginii din ruta curentă. */
function derivePageContext(pathname: string): AssistantPageContext {
  const ctx: AssistantPageContext = { route: pathname };
  const segments = pathname.split('/').filter(Boolean);
  // ex: /bookings/142 → booking #142
  const numericIdx = segments.findIndex((s) => /^\d+$/.test(s));
  if (numericIdx > 0) {
    const prev = segments[numericIdx - 1];
    const map: Record<string, string> = { bookings: 'booking', clients: 'client', offers: 'offer', containers: 'container', sedinte: 'meeting' };
    if (map[prev]) {
      ctx.entityType = map[prev];
      ctx.entityId = segments[numericIdx];
    }
  } else if (pathname.includes('/clients')) {
    ctx.entityType = 'client';
  } else if (pathname.includes('/bookings')) {
    ctx.entityType = 'booking';
  }
  return ctx;
}

/**
 * Reconstruiește mesajele de UI din turn-urile brute ale sesiunii.
 * Rezultatele tool-urilor se acumulează într-un buffer și se atașează mesajului assistant
 * care le rezumă (cel care urmează după ele) — așa chip-urile/pill-urile apar lângă textul
 * relevant, atât pentru citiri, cât și pentru acțiunile de scriere confirmate.
 */
function rebuildMessages(turns: AssistantTurn[]): ChatMessage[] {
  const messages: ChatMessage[] = [];
  let bufferedToolResults: ChatMessage['toolResults'] = [];

  for (const turn of turns) {
    if (turn.role === 'user') {
      if (turn.content && turn.content.startsWith('[SISTEM')) continue;
      messages.push({ id: nextId(), role: 'user', content: turn.content || '' });
      bufferedToolResults = [];
    } else if (turn.role === 'assistant') {
      if (turn.content) {
        messages.push({ id: nextId(), role: 'assistant', content: turn.content, toolResults: bufferedToolResults });
        bufferedToolResults = [];
      }
    } else if (turn.role === 'tool') {
      let output: unknown = turn.content;
      let ok = true;
      try {
        output = JSON.parse(turn.content || '{}');
        ok = !(output as { error?: string })?.error;
      } catch {
        /* keep raw */
      }
      // Ignoră placeholder-ele de confirmare (nu sunt rezultate reale de afișat).
      const status = (output as { status?: string })?.status;
      if (status === 'pending_confirmation' || status === 'skipped') continue;
      bufferedToolResults = [...(bufferedToolResults || []), { toolName: turn.toolName || '', ok, output }];
    }
  }
  return messages;
}

export function useAssistant() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(() => localStorage.getItem(OPEN_KEY) === '1');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [livePhase, setLivePhase] = useState<string>('');
  const [liveMessage, setLiveMessage] = useState<string>('');
  const [liveSteps, setLiveSteps] = useState<AssistantLiveStep[]>([]);
  const [liveNotes, setLiveNotes] = useState<string[]>([]);
  const liveStepSeq = useRef(0);
  const [pendingAction, setPendingAction] = useState<AssistantPendingAction | null>(null);
  const [suggestedActions, setSuggestedActions] = useState<AssistantSuggestedAction[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(SESSION_KEY));
  const [complexity, setComplexityState] = useState<AssistantComplexity>(readComplexity);
  const [conversations, setConversations] = useState<StoredConversation[]>([]);
  const rehydratedRef = useRef(false);
  const sendAbortRef = useRef<AbortController | null>(null);
  const lastSentTextRef = useRef<string>('');

  const pageContext = derivePageContext(location.pathname);

  const open = useCallback(() => {
    setIsOpen(true);
    localStorage.setItem(OPEN_KEY, '1');
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem(OPEN_KEY, '0');
  }, []);
  const toggle = useCallback(() => (isOpen ? close() : open()), [isOpen, open, close]);

  // Rehidratare istoric la prima deschidere.
  useEffect(() => {
    if (!isOpen || rehydratedRef.current || !sessionId) return;
    rehydratedRef.current = true;
    assistantService
      .getHistory(sessionId)
      .then((turns) => {
        if (turns && turns.length > 0) setMessages(rebuildMessages(turns));
      })
      .catch(() => {
        /* sesiune expirată — pornim curat */
      });
  }, [isOpen, sessionId]);

  const applyResult = useCallback((result: AssistantChatResult) => {
    if (result.sessionId) {
      setSessionId(result.sessionId);
      localStorage.setItem(SESSION_KEY, result.sessionId);
    }
    setPendingAction(result.pendingAction || null);
    setSuggestedActions(result.suggestedActions || []);
    setMessages((prev) => [
      ...prev,
      {
        id: nextId(),
        role: 'assistant',
        content: result.assistantMessage,
        toolResults: result.toolResults,
        pendingAction: result.pendingAction || null,
      },
    ]);
    // Notifică restul aplicației despre resursele modificate (live-sync):
    // paginile/tabelele/modalurile deschise vor reîmprospăta și anima entitatea atinsă.
    publishCopilotChange(result.touchedResources);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isSending) return;
      setMessages((prev) => [...prev, { id: nextId(), role: 'user', content: trimmed }]);
      setSuggestedActions([]);
      setIsSending(true);
      setLivePhase('thinking');
      setLiveMessage('Gândesc…');
      setLiveSteps([]);
      setLiveNotes([]);
      liveStepSeq.current = 0;
      const controller = new AbortController();
      sendAbortRef.current = controller;
      lastSentTextRef.current = trimmed;
      try {
        const result = await assistantService.chat({ message: trimmed, sessionId, pageContext, complexity }, controller.signal, {
          onStatus: (phase, message) => {
            setLivePhase(phase);
            if (message) setLiveMessage(message);
          },
          onTool: (payload) => {
            setLiveSteps((prev) => {
              if (payload.state === 'start') {
                liveStepSeq.current += 1;
                return [...prev, { id: `s_${liveStepSeq.current}`, name: payload.name, label: payload.label, state: 'start', detail: payload.detail }];
              }
              const next = [...prev];
              for (let i = next.length - 1; i >= 0; i--) {
                if (next[i].name === payload.name && next[i].state === 'start') {
                  next[i] = { ...next[i], state: payload.state, detail: payload.detail || next[i].detail };
                  return next;
                }
              }
              liveStepSeq.current += 1;
              return [...next, { id: `s_${liveStepSeq.current}`, name: payload.name, label: payload.label, state: payload.state, detail: payload.detail }];
            });
          },
          onNote: () => setLiveNotes((prev) => [...prev.filter((n) => n.trim()), '']),
          onDelta: (text) => {
            if (!text) return;
            setLiveNotes((prev) => {
              if (prev.length === 0) return [text];
              const next = [...prev];
              next[next.length - 1] = `${next[next.length - 1]}${text}`;
              return next;
            });
          },
        });
        if (controller.signal.aborted) return;
        if (!result.assistantMessage && !result.pendingAction) {
          setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', content: 'Nu am putut genera un răspuns. Încearcă din nou, poate pe nivelul Normal.' }]);
        } else {
          applyResult(result);
        }
      } catch (e) {
        if (controller.signal.aborted) return; // anulat de utilizator — nu afișăm eroare
        const msg = (e as Error)?.message || '';
        setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', content: msg && msg.length < 180 ? `A apărut o eroare: ${msg}` : 'A apărut o eroare la procesarea cererii. Te rog încearcă din nou.' }]);
      } finally {
        if (sendAbortRef.current === controller) sendAbortRef.current = null;
        if (!controller.signal.aborted) {
          setIsSending(false);
          setLivePhase('');
          setLiveMessage('');
          setLiveSteps([]);
          setLiveNotes([]);
        }
      }
    },
    [isSending, sessionId, pageContext, complexity, applyResult],
  );

  const setComplexity = useCallback((level: AssistantComplexity) => {
    setComplexityState(level);
    try {
      localStorage.setItem(COMPLEXITY_KEY, level);
    } catch {
      /* ignore */
    }
  }, []);

  /**
   * Anulează un mesaj aflat în curs de trimitere: abortează request-ul, elimină bula
   * utilizatorului adăugată optimist și întoarce textul, ca să poată fi repus în input.
   * Returnează null dacă nu e nimic de anulat.
   */
  const cancelSend = useCallback((): string | null => {
    const controller = sendAbortRef.current;
    if (!controller) return null;
    controller.abort();
    sendAbortRef.current = null;
    setIsSending(false);
    setLivePhase('');
    setLiveMessage('');
    setLiveSteps([]);
    setLiveNotes([]);
    setMessages((prev) => (prev.length && prev[prev.length - 1].role === 'user' ? prev.slice(0, -1) : prev));
    return lastSentTextRef.current || null;
  }, []);

  const confirmPending = useCallback(async () => {
    if (!pendingAction || isSending) return;
    setIsSending(true);
    const id = pendingAction.id;
    setPendingAction(null);
    try {
      const result = await assistantService.executePending(id);
      applyResult(result);
    } catch (e) {
      setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', content: 'Acțiunea nu a putut fi executată.' }]);
    } finally {
      setIsSending(false);
    }
  }, [pendingAction, isSending, applyResult]);

  const cancelPending = useCallback(async () => {
    if (!pendingAction) return;
    const id = pendingAction.id;
    setPendingAction(null);
    try {
      await assistantService.cancelPending(id);
    } catch {
      /* ignore */
    }
    setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', content: 'Am anulat acțiunea.' }]);
  }, [pendingAction]);

  const clear = useCallback(async () => {
    const current = sessionId;
    setMessages([]);
    setPendingAction(null);
    setSuggestedActions([]);
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
    rehydratedRef.current = false;
    if (current) {
      try {
        await assistantService.clearSession(current);
      } catch {
        /* ignore */
      }
    }
  }, [sessionId]);

  // Salvează automat conversația curentă în istoricul local (pentru căutare/reluare ulterioară).
  useEffect(() => {
    if (sessionId && messages.length > 0) saveConversation(sessionId, messages);
  }, [messages, sessionId]);

  /** Reîncarcă lista de conversații din store (de apelat când se deschide dropdown-ul). */
  const refreshConversations = useCallback(() => {
    setConversations(listConversations());
  }, []);

  /** Încarcă o conversație din istoric: o afișează instant din local, apoi încearcă rehidratarea de pe server. */
  const loadConversation = useCallback((id: string) => {
    const stored = getConversation(id);
    if (!stored) return;
    setSessionId(id);
    localStorage.setItem(SESSION_KEY, id);
    setMessages(stored.messages);
    setPendingAction(null);
    setSuggestedActions([]);
    rehydratedRef.current = true;
    // Dacă sesiunea încă există pe server (TTL 1h), folosim istoricul lui (mai proaspăt).
    assistantService
      .getHistory(id)
      .then((turns) => {
        if (turns && turns.length > 0) setMessages(rebuildMessages(turns));
      })
      .catch(() => {
        /* sesiune expirată pe server — rămânem cu snapshotul local */
      });
  }, []);

  /** Șterge o conversație din istoric; dacă e cea curentă, pornește una nouă. */
  const removeConversation = useCallback(
    (id: string) => {
      deleteStoredConversation(id);
      setConversations(listConversations());
      if (id === sessionId) {
        setMessages([]);
        setPendingAction(null);
        setSuggestedActions([]);
        setSessionId(null);
        localStorage.removeItem(SESSION_KEY);
        rehydratedRef.current = false;
      }
    },
    [sessionId],
  );

  /** Pornește o conversație nouă, păstrând-o pe cea curentă în istoric. */
  const newConversation = useCallback(() => {
    setMessages([]);
    setPendingAction(null);
    setSuggestedActions([]);
    setSessionId(null);
    localStorage.removeItem(SESSION_KEY);
    rehydratedRef.current = false;
  }, []);

  return {
    isOpen,
    open,
    close,
    toggle,
    messages,
    isSending,
    livePhase,
    liveMessage,
    liveSteps,
    liveNotes,
    pendingAction,
    suggestedActions,
    sessionId,
    pageContext,
    send,
    complexity,
    setComplexity,
    cancelSend,
    confirmPending,
    cancelPending,
    clear,
    conversations,
    refreshConversations,
    loadConversation,
    removeConversation,
    newConversation,
  };
}
